import { useState } from 'react';

// ── Mode ───────────────────────────────────────────────────────────────────────

/** Four exclusive modes of a master maintenance screen. */
export type MasterMode = 'initial' | 'new' | 'view' | 'edit';

// ── Toolbar slot states ────────────────────────────────────────────────────────

/**
 * Button enable/disable state for MasterToolbar.
 * Note: there is no `newEnabled` — master files create records via code lookup,
 * not via a dedicated New button.
 */
export interface MasterToolbarState {
  saveEnabled:    boolean;
  editEnabled:    boolean;
  deleteEnabled:  boolean;
  printEnabled:   boolean;
  /** Refresh resets to INITIAL; it is always enabled regardless of mode. */
  refreshEnabled: boolean;
}

// ── Hook options ───────────────────────────────────────────────────────────────

interface UseMasterScreenStateOptions {
  /** User has EDIT permission for this entity */
  canEdit?: boolean;
  /** User has DELETE permission for this entity */
  canDelete?: boolean;
  /** User has PRINT permission (default: true) */
  canPrint?: boolean;
}

// ── Return type ────────────────────────────────────────────────────────────────

export interface MasterScreenState {
  mode:      MasterMode;
  setMode:   (mode: MasterMode) => void;

  // Convenience booleans
  isInitial: boolean;
  isNew:     boolean;
  isView:    boolean;
  isEdit:    boolean;

  /** True when a record is displayed (view or edit). */
  hasRecord: boolean;

  /** Ready-to-spread props for MasterToolbar. */
  toolbar:   MasterToolbarState;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useMasterScreenState({
  canEdit   = false,
  canDelete = false,
  canPrint  = true,
}: UseMasterScreenStateOptions = {}): MasterScreenState {
  const [mode, setMode] = useState<MasterMode>('initial');

  const isInitial = mode === 'initial';
  const isNew     = mode === 'new';
  const isView    = mode === 'view';
  const isEdit    = mode === 'edit';
  const hasRecord = isView || isEdit;

  /**
   * Button state matrix
   * ┌──────────┬──────┬───────┬────────┬───────┬─────────┐
   * │ Mode     │ Save │ Edit  │ Delete │ Print │ Refresh │
   * ├──────────┼──────┼───────┼────────┼───────┼─────────┤
   * │ INITIAL  │  off │  off  │  off   │  off  │   on    │
   * │ NEW      │  on  │  off  │  off   │  off  │   on    │
   * │ VIEW     │  off │  on*  │  on*   │  on*  │   on    │
   * │ EDIT     │  on  │  off  │  off   │  off  │   on    │
   * └──────────┴──────┴───────┴────────┴───────┴─────────┘
   * (* = subject to permission)
   * Refresh is always enabled — it always resets to INITIAL blank state.
   */
  const toolbar: MasterToolbarState = {
    saveEnabled:    isNew || isEdit,
    editEnabled:    isView && canEdit,
    deleteEnabled:  isView && canDelete,
    printEnabled:   isView && canPrint,
    refreshEnabled: true,
  };

  return {
    mode,
    setMode,
    isInitial,
    isNew,
    isView,
    isEdit,
    hasRecord,
    toolbar,
  };
}
